import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Button,
  Flex,
  Badge,
  VStack,
  HStack,
  useToast,
  Avatar,
  Divider,
  Progress,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  Spinner,
  Center,
  IconButton,
} from "@chakra-ui/react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiMail,
  FiCalendar,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiUser,
  FiFileText,
  FiDownload,
  FiEye,
  FiMessageSquare,
} from "react-icons/fi";
import { useParams, useNavigate } from "react-router-dom";
import { applicationService, atsUtils } from "../atsService";
import CreateOfferModal from "../../../components/CreateOfferModal";

const ApplicationDetails = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isCreateOfferModalOpen, setIsCreateOfferModalOpen] = useState(false);
  const [isNextStageModalOpen, setIsNextStageModalOpen] = useState(false);
  const [nextStageOptions, setNextStageOptions] = useState([]);
  const [nextStageLoading, setNextStageLoading] = useState(false);
  const [selectedNextStage, setSelectedNextStage] = useState("");
  const [emailData, setEmailData] = useState({
    subject: "",
    message: "",
    template: "interview_invitation",
  });

  // Application stages in order
  const stages = [
    "applied",
    "screening",
    "phone_screen",
    "assessment",
    "interview_1",
    "interview_2",
    "offer_pending",
    "hired",
  ];

  useEffect(() => {
    fetchApplicationDetails();
  }, [applicationId]);

  const fetchApplicationDetails = async () => {
    try {
      setLoading(true);
      const response = await applicationService.getApplication(applicationId);
      setApplication(response.data.data);
    } catch (error) {
      console.error("Error fetching application:", error);
      toast({
        title: "Error",
        description: "Failed to fetch application details",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      await applicationService.updateApplicationStage(applicationId, {
        newStage,
      });
      await fetchApplicationDetails(); // Refresh the application data
      toast({
        title: "Success",
        description: "Application stage updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update application stage",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSendEmail = async () => {
    try {
      await applicationService.sendEmail(applicationId, emailData);
      setIsEmailModalOpen(false);
      toast({
        title: "Success",
        description: "Email sent successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send email",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleScheduleInterview = () => {
    // Navigate to interview scheduling or open modal
    toast({
      title: "Feature Coming Soon",
      description: "Interview scheduling will be available soon",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  const getStageProgress = () => {
    const currentIndex = stages.indexOf(application?.currentStage);
    return currentIndex >= 0 ? ((currentIndex + 1) / stages.length) * 100 : 0;
  };

  const getTimelineEvents = () => {
    if (!application) return [];

    const events = [
      {
        date: application.appliedAt,
        title: "Application Submitted",
        description: `Applied for ${
          application.requisitionId?.jobTitle || "Position"
        }`,
        type: "application",
        icon: FiFileText,
      },
    ];

    // Add stage changes from history
    if (application.stageHistory) {
      application.stageHistory.forEach((history) => {
        events.push({
          date: history.enteredAt,
          title: `Moved to ${atsUtils.formatApplicationStatus(history.stage)}`,
          description:
            history.notes ||
            `Stage changed by ${history.updatedBy?.name || "System"}`,
          type: "stage_change",
          icon: FiCheckCircle,
        });
      });
    }

    // Add interviews
    if (application.interviews) {
      application.interviews.forEach((interview) => {
        events.push({
          date: interview.scheduledAt,
          title: `${interview.type} Interview`,
          description: `Scheduled with ${
            interview.interviewers
              ?.map((i) => i.interviewerName || i.interviewerId?.name)
              .join(", ") || "Interviewers"
          }`,
          type: "interview",
          icon: FiCalendar,
        });

        if (interview.completedAt) {
          events.push({
            date: interview.completedAt,
            title: "Interview Completed",
            description: `Feedback: ${
              interview.feedback?.overallRating || "Pending"
            }`,
            type: "interview_complete",
            icon: FiCheckCircle,
          });
        }
      });
    }

    // Sort by date
    return events.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  if (!application) {
    return (
      <Center h="400px">
        <Text>Application not found</Text>
      </Center>
    );
  }

  const timelineEvents = getTimelineEvents();

  return (
    <Box p={6}>
      <Flex mb={6} justify="space-between" align="center">
        <HStack>
          <IconButton
            icon={<FiChevronLeft />}
            onClick={() => navigate(-1)}
            aria-label="Go back"
          />
          <VStack align="start" spacing={1}>
            <Heading size="lg">
              {application.candidateId?.firstName}{" "}
              {application.candidateId?.lastName}
            </Heading>
            <Text color="gray.600">
              {application.requisitionId?.jobTitle} •{" "}
              {application.requisitionId?.department}
            </Text>
          </VStack>
        </HStack>
        <HStack spacing={4}>
          <Button
            leftIcon={<FiMail />}
            colorScheme="blue"
            onClick={() => setIsEmailModalOpen(true)}
          >
            Send Email
          </Button>
          <Button
            leftIcon={<FiCalendar />}
            colorScheme="green"
            onClick={handleScheduleInterview}
          >
            Schedule Interview
          </Button>
          <Button
            leftIcon={<FiChevronRight />}
            colorScheme="teal"
            onClick={() => {
              const currentIndex = stages.indexOf(application?.currentStage);
              if (currentIndex === -1 || currentIndex >= stages.length - 1) {
                toast({
                  title: "Cannot advance",
                  description: "Application is at the final stage",
                  status: "info",
                  duration: 3000,
                });
                return;
              }
              const nextStage = stages[currentIndex + 1];
              handleStageChange(nextStage);
            }}
          >
            Move To Next Stage
          </Button>
          {["final_interview", "offer_pending"].includes(
            application.currentStage
          ) && (
            <Button
              leftIcon={<FiFileText />}
              colorScheme="purple"
              onClick={() => setIsCreateOfferModalOpen(true)}
            >
              Create Offer
            </Button>
          )}
        </HStack>
      </Flex>

      <Grid templateColumns="repeat(3, 1fr)" gap={6} mb={6}>
        <Card>
          <CardBody>
            <VStack align="start">
              <Text color="gray.600">Current Stage</Text>
              <Button
                leftIcon={<FiChevronRight />}
                colorScheme="teal"
                onClick={async () => {
                  // Open modal and fetch allowed next stages from backend
                  setIsNextStageModalOpen(true);
                  try {
                    setNextStageLoading(true);
                    // try dedicated endpoint for next stages
                    const ns = await applicationService.getNextStages(
                      applicationId
                    );

                    const options = ns.data?.next || ns.data?.next || [];
                    setNextStageOptions(Array.isArray(options) ? options : []);
                    setSelectedNextStage(options[0] || "");
                  } catch (err) {
                    console.error("Failed to fetch next stages", err);
                    toast({
                      title: "Error",
                      description: "Failed to load next stage options",
                      status: "error",
                    });
                    setNextStageOptions([]);
                  } finally {
                    setNextStageLoading(false);
                  }
                }}
              >
                Move To Next Stage
              </Button>
              <Text fontWeight="medium">
                {new Date(application.appliedAt).toLocaleDateString()}
              </Text>
            </VStack>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <VStack align="start">
              <Text color="gray.600">Last Updated</Text>
              <Text fontWeight="medium">
                {new Date(application.updatedAt).toLocaleDateString()}
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </Grid>

      <Grid templateColumns="repeat(2, 1fr)" gap={6}>
        <Card>
          <CardHeader>
            <Heading size="md">Candidate Information</Heading>
          </CardHeader>
          <CardBody>
            <VStack align="start" spacing={4}>
              <HStack spacing={4}>
                <Avatar
                  size="lg"
                  name={`${application.candidateId?.firstName} ${application.candidateId?.lastName}`}
                />
                <VStack align="start" spacing={1}>
                  <Text fontSize="lg" fontWeight="medium">
                    {application.candidateId?.firstName}{" "}
                    {application.candidateId?.lastName}
                  </Text>
                  <Text color="gray.600">{application.candidateId?.email}</Text>
                  <Text color="gray.600">{application.candidateId?.phone}</Text>
                </VStack>
              </HStack>

              <Divider />

              <VStack align="start" spacing={2} w="full">
                <Text fontWeight="medium">Professional Info</Text>
                <Text fontSize="sm" color="gray.600">
                  Current Title:{" "}
                  {application.candidateId?.currentTitle || "Not specified"}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Current Company:{" "}
                  {application.candidateId?.currentCompany || "Not specified"}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Experience:{" "}
                  {application.candidateId?.yearsOfExperience ||
                    "Not specified"}{" "}
                  years
                </Text>
              </VStack>

              {application.resumeId && (
                <>
                  <Divider />
                  <VStack align="start" spacing={2} w="full">
                    <Text fontWeight="medium">Resume</Text>
                    <HStack>
                      <Button
                        leftIcon={<FiDownload />}
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          window.open(application.resumeId.fileUrl, "_blank")
                        }
                      >
                        Download Resume
                      </Button>
                      <Text fontSize="sm" color="gray.600">
                        Quality Score:{" "}
                        {application.resumeId.qualityScore || "N/A"}/100
                      </Text>
                    </HStack>
                  </VStack>
                </>
              )}
            </VStack>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <Heading size="md">Application Progress</Heading>
          </CardHeader>
          <CardBody>
            <VStack align="start" spacing={6}>
              {/* Stage Progress Steps */}
              <VStack align="start" spacing={4} w="full">
                <Text fontWeight="medium">Application Stages</Text>
                <VStack spacing={3} w="full">
                  {[
                    "applied",
                    "screening",
                    "phone_screen",
                    "assessment",
                    "interview_1",
                    "interview_2",
                    "offer_pending",
                    "hired",
                  ].map((stage, index) => {
                    const isCompleted =
                      stages.indexOf(application?.currentStage) > index;
                    const isCurrent = application?.currentStage === stage;
                    const isUpcoming =
                      stages.indexOf(application?.currentStage) < index;

                    return (
                      <HStack key={stage} spacing={4} w="full">
                        <Box
                          position="relative"
                          minW="24px"
                          minH="24px"
                          borderRadius="full"
                          border="2px solid"
                          borderColor={
                            isCompleted
                              ? "green.500"
                              : isCurrent
                              ? "blue.500"
                              : "gray.300"
                          }
                          bg={isCompleted ? "green.500" : "white"}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {isCompleted ? (
                            <FiCheckCircle size={12} color="white" />
                          ) : isCurrent ? (
                            <Box
                              w="8px"
                              h="8px"
                              bg="blue.500"
                              borderRadius="full"
                            />
                          ) : (
                            <Box
                              w="8px"
                              h="8px"
                              bg="gray.300"
                              borderRadius="full"
                            />
                          )}
                        </Box>

                        <VStack align="start" spacing={0} flex={1}>
                          <Text
                            fontSize="sm"
                            fontWeight={isCurrent ? "medium" : "normal"}
                            color={
                              isCompleted
                                ? "green.600"
                                : isCurrent
                                ? "blue.600"
                                : "gray.600"
                            }
                          >
                            {atsUtils.formatApplicationStatus(stage)}
                          </Text>
                          {isCurrent && (
                            <Text fontSize="xs" color="blue.500">
                              Current Stage
                            </Text>
                          )}
                        </VStack>
                      </HStack>
                    );
                  })}
                </VStack>
              </VStack>

              <Divider />

              {/* Progress Bar */}
              <Box w="full">
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.600">
                    Overall Progress
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {Math.round(getStageProgress())}%
                  </Text>
                </HStack>
                <Progress value={getStageProgress()} colorScheme="blue" />
              </Box>

              {/* Stage History */}
              <VStack align="start" spacing={3} w="full">
                <Text fontWeight="medium">Recent Stage Changes</Text>
                {application.stageHistory?.slice(0, 5).map((history, index) => (
                  <HStack key={index} w="full" justify="space-between">
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium">
                        {atsUtils.formatApplicationStatus(history.stage)}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {new Date(history.enteredAt).toLocaleDateString()}
                      </Text>
                    </VStack>
                    <Badge
                      colorScheme={atsUtils.getStatusColor(history.stage)}
                      variant="subtle"
                    >
                      {history.updatedBy?.name || "System"}
                    </Badge>
                  </HStack>
                )) || (
                  <Text fontSize="sm" color="gray.500">
                    No stage history available
                  </Text>
                )}
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      </Grid>

      <Card mt={6}>
        <CardHeader>
          <Heading size="md">Timeline & Activity</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={6} align="stretch">
            {timelineEvents.map((event, index) => (
              <Box key={index} position="relative">
                {/* Timeline line */}
                {index < timelineEvents.length - 1 && (
                  <Box
                    position="absolute"
                    left="18px"
                    top="40px"
                    bottom="-30px"
                    width="2px"
                    bg="blue.100"
                    zIndex={1}
                  />
                )}

                <HStack align="start" spacing={4}>
                  {/* Timeline dot */}
                  <Box
                    position="relative"
                    zIndex={2}
                    bg="blue.500"
                    borderRadius="full"
                    p={2}
                    minW="36px"
                    minH="36px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <event.icon size={16} color="white" />
                  </Box>

                  {/* Timeline content */}
                  <VStack align="start" spacing={1} flex={1}>
                    <HStack justify="space-between" w="full">
                      <Text fontWeight="medium">{event.title}</Text>
                      <Text fontSize="sm" color="gray.500">
                        {new Date(event.date).toLocaleDateString()}
                      </Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                      {event.description}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            ))}
          </VStack>
        </CardBody>
      </Card>

      {/* Email Modal */}
      <Modal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Send Email to Candidate</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Email Template</FormLabel>
                <Select
                  value={emailData.template}
                  onChange={(e) =>
                    setEmailData({ ...emailData, template: e.target.value })
                  }
                >
                  <option value="interview_invitation">
                    Interview Invitation
                  </option>
                  <option value="offer_letter">Offer Letter</option>
                  <option value="rejection">Rejection Notice</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="custom">Custom</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Subject</FormLabel>
                <Textarea
                  value={emailData.subject}
                  onChange={(e) =>
                    setEmailData({ ...emailData, subject: e.target.value })
                  }
                  placeholder="Enter email subject"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Message</FormLabel>
                <Textarea
                  value={emailData.message}
                  onChange={(e) =>
                    setEmailData({ ...emailData, message: e.target.value })
                  }
                  placeholder="Enter email message"
                  minH="200px"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => setIsEmailModalOpen(false)}
            >
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSendEmail}>
              Send Email
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Offer Modal */}
      <CreateOfferModal
        isOpen={isCreateOfferModalOpen}
        onClose={() => setIsCreateOfferModalOpen(false)}
        preSelectedApplication={application}
        onSuccess={() => {
          setIsCreateOfferModalOpen(false);
          fetchApplicationDetails(); // Refresh application data
          toast({
            title: "Success",
            description: "Offer created successfully",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        }}
      />

      {/* Next Stage Modal */}
      <Modal
        isOpen={isNextStageModalOpen}
        onClose={() => setIsNextStageModalOpen(false)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Move Application to Next Stage</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {nextStageLoading ? (
              <Center>
                <Spinner />
              </Center>
            ) : nextStageOptions.length > 0 ? (
              <FormControl>
                <FormLabel>Select next stage</FormLabel>
                <Select
                  value={selectedNextStage}
                  onChange={(e) => setSelectedNextStage(e.target.value)}
                >
                  {nextStageOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {atsUtils.formatApplicationStatus(opt)}
                    </option>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Text>No next stages available</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => setIsNextStageModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              colorScheme="teal"
              onClick={async () => {
                if (!selectedNextStage)
                  return toast({ title: "Select a stage", status: "warning" });
                await handleStageChange(selectedNextStage);
                setIsNextStageModalOpen(false);
              }}
            >
              Move
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ApplicationDetails;
